import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import ErrorDisplay from './ErrorDisplay';
import LoadingSpinner from './LoadingSpinner';
import AuthLanguageSwitcher from './AuthLanguageSwitcher';
import { WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { createLoginPasswordSchema } from '../../lib/passwordPolicy';
import { AUTH_ERROR_CODES, ValidationError } from '../../types/auth';
import MfaFlow from './MfaFlow';

type LoginFormData = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

interface LoginFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  redirectTo = '/admin'
}) => {
  const { login, isLoading, error, clearError, clearRateLimit, isAuthenticated, retryLastAction, resendVerification, completeMfaLogin } = useAuth();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const [resendSent, setResendSent] = useState(false);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  // Debounce rapid Enter/click submissions to avoid pre-429 request spam.
  const [lastSubmitAt, setLastSubmitAt] = useState<number>(0);

  // SEC task 5: an enrolled user (any role) must complete a TOTP step
  // before the backend establishes a session.
  const [mfa, setMfa] = useState<{ mode: 'verify' | 'setup'; challenge: string } | null>(null);

  // Validation schema depends on the active language.
  const loginSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t('validation.emailRequired'))
          .email(t('validation.emailInvalid')),
        password: createLoginPasswordSchema(t),
        rememberMe: z.boolean().optional(),
      }),
    [t]
  );

  // Initialize form with react-hook-form and zod validation
  const {
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  // Clear error when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Load and tick down any active rate-limit lockout for the login form.
  useEffect(() => {
    const stored = sessionStorage.getItem('loginRateLimitUntil');
    if (stored) {
      const until = parseInt(stored, 10);
      if (!isNaN(until) && until > Date.now()) {
        setRateLimitUntil(until);
      } else {
        clearRateLimit();
      }
    }
  }, [clearRateLimit]);

  useEffect(() => {
    if (!rateLimitUntil) {
      setRemainingSeconds(0);
      return;
    }
    const update = () => {
      const left = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000));
      setRemainingSeconds(left);
      if (left === 0) {
        clearRateLimit();
      }
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [rateLimitUntil, clearRateLimit]);

  // Redirect if already authenticated
  // Preserve the original location if available (from ProtectedRoute redirect)
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname;
      const targetPath = from || redirectTo;
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo, location]);

  const SUBMIT_DEBOUNCE_MS = 1500;

  const onSubmit = async (data: LoginFormData) => {
    if (remainingSeconds > 0 || isLoading) return;

    const now = Date.now();
    if (now - lastSubmitAt < SUBMIT_DEBOUNCE_MS) return;
    setLastSubmitAt(now);

    try {
      const response = await login(data.email, data.password, data.rememberMe);

      // No session yet — hand over to the TOTP step.
      if (response && (response.mfaRequired || response.mfaSetupRequired)) {
        setMfa({
          mode: response.mfaSetupRequired ? 'setup' : 'verify',
          challenge: response.challenge || '',
        });
        return;
      }

      onSuccess?.();
    } catch (error) {
      const authError = error as any;
      // Keep the submit button disabled for the full lockout window.
      const until = authError?.details?.retryAfterUntil;
      if (typeof until === 'number' && until > Date.now()) {
        setRateLimitUntil(until);
      }

      // Surface server-side field errors next to their inputs when available.
      const fieldErrors = authError?.details?.errors as ValidationError[] | undefined;
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(({ field, message }) => {
          if (field === 'email' || field === 'password') {
            setError(field, { type: 'manual', message: message || t('common.invalidValue') });
          }
        });
      }
    }
  };

  const handleMfaVerified = (user: Parameters<typeof completeMfaLogin>[0]) => {
    completeMfaLogin(user);
    setMfa(null);
    onSuccess?.();
  };

  return (
    <div className="auth-background">
      <div className="auth-background-overlay"></div>
      <AuthLanguageSwitcher />
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo-commission-fr.png" alt="CNCLT" />
          </div>
          <h1 className="auth-title">{t('login.title')}</h1>
          <p className="auth-subtitle">{t('login.subtitle')}</p>
        </div>

        <ErrorDisplay
          error={error}
          onRetry={retryLastAction}
          onDismiss={clearError}
          action={
            error?.code === AUTH_ERROR_CODES.INVALID_CREDENTIALS ? (
              <Link to="/forgot-password" className="auth-link inline-flex items-center gap-2 text-sm">
                {t('login.forgotPassword')}
              </Link>
            ) : undefined
          }
        />

        {error?.code === AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED && (
          <div className="text-center mb-2">
            <button
              type="button"
              className="auth-link text-sm"
              disabled={isLoading}
              onClick={async () => {
                const email = getValues('email');
                if (!email) return;
                try {
                  await resendVerification(email);
                  setResendSent(true);
                } catch {
                  // handled via ErrorDisplay
                }
              }}
            >
              {t('login.resendVerification')}
            </button>
            {resendSent && (
              <div className="auth-alert auth-alert-success mt-4" role="status">
                <span className="auth-alert-icon">
                  <CheckCircle size={20} weight="fill" aria-hidden />
                </span>
                <p className="auth-alert-body">
                  {t('login.resendSent')}
                </p>
              </div>
            )}
          </div>
        )}

        {mfa ? (
          <MfaFlow
            challenge={mfa.challenge}
            mode={mfa.mode}
            isLoading={isLoading}
            onVerified={handleMfaVerified}
          />
        ) : (
        <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="auth-form-group">
            <label className="auth-form-label" htmlFor="email">{t('common.emailLabel')}</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`auth-form-input ${errors.email ? 'auth-form-input-error' : ''}`}
              placeholder={t('common.emailPlaceholder')}
              disabled={isLoading || remainingSeconds > 0}
              {...register('email')}
            />
            {errors.email && (
              <p className="auth-form-error" role="alert">
                <WarningCircle size={14} weight="bold" aria-hidden />
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="auth-form-group">
            <label className="auth-form-label" htmlFor="password">{t('common.passwordLabel')}</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className={`auth-form-input ${errors.password ? 'auth-form-input-error' : ''}`}
              placeholder={t('common.passwordPlaceholder')}
              disabled={isLoading || remainingSeconds > 0}
              {...register('password')}
            />
            {errors.password && (
              <p className="auth-form-error" role="alert">
                <WarningCircle size={14} weight="bold" aria-hidden />
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center" htmlFor="remember-me">
              <input
                id="remember-me"
                type="checkbox"
                className="auth-checkbox mr-2"
                disabled={isLoading || remainingSeconds > 0}
                {...register('rememberMe')}
              />
              <span className="text-sm text-slate-700">{t('login.rememberMe')}</span>
            </label>
            <Link to="/forgot-password" className="auth-link text-sm">
              {t('login.forgotPassword')}
            </Link>
          </div>

          <p className="text-xs text-slate-500">
            {t('login.rememberHint')}
          </p>

          <button
            type="submit"
            className={`auth-button-primary ${isLoading || remainingSeconds > 0 ? 'auth-loading' : ''}`}
            disabled={isLoading || remainingSeconds > 0}
          >
            {isLoading ? (
              <LoadingSpinner size="sm" text={t('login.submitting')} />
            ) : remainingSeconds > 0 ? (
              t('login.retryIn', { minutes: Math.ceil(remainingSeconds / 60) })
            ) : (
              t('login.submit')
            )}
          </button>
        </form>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
